import React, { useEffect, useState, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import {
  Box,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AddCircleOutline, CloudUpload, Help as HelpIcon, RemoveCircleOutline } from '@mui/icons-material';
import moment from 'moment';
import { generatePath, Link } from 'react-router-dom';
import Bluebird from 'bluebird';
import useHasActiveFeatureFlag from '../../../hooks/useFeatureFlags';
import { FF_SD_DASHBOARD_SETTINGS_PHONE_NUMBERS, FF_SD_DASHBOARD_SETTINGS_SUPPLEMENTAL_REGISTRATION_DOCUMENT } from '../../../components/FeatureFlagWrapper';
import { Colors } from '../../../assets/theme/Colors';
import PhoneMask from '../../../components/PhoneMask';
import { DashboardTableCard } from '../../../components/Dashboard/DashboardTableCard';
import HtmlTooltip from '../../../components/Common/HtmlTooltip';
import routes from '../../../routes';
import { fetchSite, patchSite } from '../../../actions/sites';
import { isInPWAView } from '../../../components/Add2HS/PWAInstallButton';
import { generateS3Url, s3FileUpload } from '../../../api/upload';

const headerTitleSx = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: '0.25em',
  width: 'fit-content',
  height: '2em',
  margin: '-1em 0.5em 0em 0.5em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '1em',
  fontWeight: 600,
};

const BorderedSection = ({ title, children }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        borderLeft: '1px solid black',
        borderBottom: '1px solid black',
        borderRight: '1px solid black',
        borderRadius: '5px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
        <Box sx={{ borderTop: '1px solid black', width: '1em', borderTopLeftRadius: '5px' }} />
        {title && <Box sx={headerTitleSx}>{title && <span>{title}</span>}</Box>}
        <Box sx={{ borderTop: '1px solid black', width: '1em', flexGrow: 2, borderTopRightRadius: '5px' }} />
      </Box>
      <Box sx={{ paddingBottom: '1em', paddingLeft: '1em', paddingRight: '1em' }}>{children}</Box>
    </Box>
  );
};

const SCDashboardSettings = ({ siteId }) => {
  const dispatch = useDispatch();
  const sitesEntities = useSelector((state) => state.entities.sites);
  const site = sitesEntities.byId[siteId] || {};
  const supplementalDocument = site?.rescuer_supplemental_registration_document;
  const isChecked = !!supplementalDocument;
  const [isSupplementalDocChecked, setIsSupplementalDocChecked] = useState(isChecked || false);
  const [loading, setLoading] = useState(false);
  const [documentName, setDocumentName] = useState(supplementalDocument?.name || '');
  const isSupplementalDoc = !!supplementalDocument;
  const hasPhoneNumbersFF = useHasActiveFeatureFlag(FF_SD_DASHBOARD_SETTINGS_PHONE_NUMBERS);
  const hasSupplementalRegDocFF = useHasActiveFeatureFlag(FF_SD_DASHBOARD_SETTINGS_SUPPLEMENTAL_REGISTRATION_DOCUMENT);
  const [phoneNumber, setPhoneNumber] = useState(site?.phone_number || '');
  const [routeToPhones, setRouteToPhones] = useState(site?.route_to_phone_numbers || ['']);
  const [routeToTouched, setRouteToTouched] = useState(false);

  useEffect(() => {
    dispatch(fetchSite(siteId));
  }, []);

  useEffect(() => {
    setIsSupplementalDocChecked(isChecked);
  }, [isChecked]);

  useEffect(() => {
    setDocumentName(supplementalDocument?.name || '');
  }, [supplementalDocument?.name]);

  useEffect(() => {
    setPhoneNumber(site?.phone_number || '');
  }, [site?.phone_number]);

  useEffect(() => {
    const phones = site?.route_to_phone_numbers;
    if (phones && phones.length > 0) {
      setRouteToPhones(phones);
    } else {
      setRouteToPhones(['']);
    }
  }, [JSON.stringify(site?.route_to_phone_numbers)]);

  const savePhoneNumber = async (value) => {
    setLoading(true);
    try {
      await dispatch(patchSite(siteId, { phone_number: value || null }));
      await dispatch(fetchSite(siteId));
    } finally {
      setLoading(false);
    }
  };

  const saveRouteToPhones = async (phones) => {
    const filtered = phones.filter((p) => p.trim() !== '');
    setLoading(true);
    try {
      await dispatch(patchSite(siteId, { route_to_phone_numbers: filtered }));
      await dispatch(fetchSite(siteId));
    } finally {
      setLoading(false);
    }
  };

  const handleRouteToPhoneChange = (index, value) => {
    const updated = [...routeToPhones];
    updated[index] = value;
    setRouteToPhones(updated);
  };

  const handleAddRouteToPhone = () => {
    setRouteToPhones([...routeToPhones, '']);
  };

  const handleRemoveRouteToPhone = (index) => {
    const updated = routeToPhones.filter((_, i) => i !== index);
    if (updated.length === 0) {
      updated.push('');
    }
    setRouteToPhones(updated);
    saveRouteToPhones(updated);
  };

  const updateSupplementalDocument = async (values) => {
    await dispatch(patchSite(siteId, {
      rescuer_supplemental_registration_document: values
    }));

    return dispatch(fetchSite(siteId));
  };

  const handleSupplementalCheck = (event) => {
    const { checked } = event.target;

    setIsSupplementalDocChecked(checked);

    if (isSupplementalDoc) {
      return updateSupplementalDocument(null);
    }
  };

  const debouncedSaveRef = useRef();

  const saveDocumentName = useCallback((name) => {
    if (!supplementalDocument || name === supplementalDocument?.name) {
      return;
    }

    setLoading(true);
    updateSupplementalDocument({
      name,
    }).finally(() => setLoading(false));
  }, [supplementalDocument]);

  useEffect(() => {
    debouncedSaveRef.current = debounce(saveDocumentName, 800);
    return () => {
      debouncedSaveRef.current?.cancel();
    };
  }, [saveDocumentName]);

  const handleDocumentNameChange = (event) => {
    const newName = event.target.value;
    setDocumentName(newName);
    debouncedSaveRef.current?.(newName);
  };

  const handleDocUpload = (file) => {
    setLoading(true);

    return Bluebird.try(() => generateS3Url(file.name, 'sites-assets', { contentDisposition: 'inline' }))
      .then((res) => res.json())
      .then((res) => res.data)
      .then((signedData) =>
        Bluebird.try(() =>
          s3FileUpload({
            url: signedData.url,
            file: file.file,
            contentType: signedData.contentType,
            contentDisposition: signedData.contentDisposition,
          })
            .then(() => {
              const matches = signedData.url.match(/(.*)\?/);
              const newData = { ...file };

              if (matches && Array.isArray(matches) && matches[1]) {
                newData.src = matches[1];
              }

              return updateSupplementalDocument({
                name: signedData.name,
                key: signedData.key,
                url: newData.src,
              });
            })
            .finally(() => setLoading(false))
        )
      );
  };

  const readFiles = (event = '') => {
    event.preventDefault();
    const fileTmp = event.target.files[0];
    const file = {
      ...fileTmp,
      title: fileTmp.name.replace(/\.[^/.]+$/, ''), // trim extension
      date: moment().format('YYYY-MM-DD'),
      confirmed: false,
      active: true,
      name: fileTmp.name,
      id: fileTmp.name,
      src: URL.createObjectURL(fileTmp),
      file: fileTmp,
    };

    handleDocUpload(file);
  };

  return (
    <>
      <Box mb={2}>
        <Typography variant="h6" color="textSecondary">
          Settings
        </Typography>
      </Box>

      {hasPhoneNumbersFF && site?.phone_number && (
        <>
          <DashboardTableCard isLoading={sitesEntities.inflight || loading}>
            <CardContent>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                Contact Phone# {' '}
                <Chip label="NEW" size="small" color="primary" style={{ height: 16, fontSize: 10, fontWeight: 700, verticalAlign: 'middle' }} />
              </Typography>
              <Box sx={{ maxWidth: 360 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Phone Number
                </Typography>
                <Box display="flex" alignItems="center" gap="4px">
                  <TextField
                    id="site-phone-number"
                    variant="outlined"
                    size="small"
                    placeholder="(999) 888-7777"
                    fullWidth
                    disabled
                    value={phoneNumber}
                  />
                  <Tooltip arrow placement="top" title="This is the public phone number for your site. Incoming calls will be forwarded to the 'Route To' numbers below. Contact your admin to change this number.">
                    <HelpIcon htmlColor={Colors.blue.main} />
                  </Tooltip>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                  Route To
                </Typography>
                {routeToPhones.map((phone, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={1} gap="4px">
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder="000-000-0000"
                      fullWidth
                      value={phone}
                      onChange={(e) => handleRouteToPhoneChange(index, e.target.value)}
                      onBlur={() => { setRouteToTouched(true); saveRouteToPhones(routeToPhones); }}
                      error={routeToTouched && phoneNumber && !routeToPhones.some((p) => p.trim() !== '') && index === 0}
                      helperText={
                        routeToTouched && phoneNumber && !routeToPhones.some((p) => p.trim() !== '') && index === 0
                          ? 'Required'
                          : ''
                      }
                      InputProps={{ inputComponent: PhoneMask }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveRouteToPhone(index)}
                      disabled={routeToPhones.length <= 1}
                    >
                      <RemoveCircleOutline fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleAddRouteToPhone}
                      sx={index < routeToPhones.length - 1 ? { visibility: 'hidden' } : {}}
                    >
                      <AddCircleOutline />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </DashboardTableCard>

          <Box mt={2} />
        </>
      )}

      {hasSupplementalRegDocFF && (
        <DashboardTableCard isLoading={sitesEntities.inflight || loading}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.1rem',
              }}
            >
              Rescuers
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8} md={5} lg={4}>
                <FormControlLabel
                  control={
                    <Switch
                      color="secondary"
                      checked={isSupplementalDocChecked}
                      name="supplemental_registration_document"
                      onChange={handleSupplementalCheck}
                    />
                  }
                  label="Supplemental Registration Document"
                />

                {isSupplementalDocChecked && (
                  <>
                    <Box display="flex" alignItems="center">
                      {!isSupplementalDoc && (
                        <HtmlTooltip title="Upload file">
                          <IconButton color="secondary" component="label" variant="contained">
                            <input
                              accept=".pdf"
                              name="supplemental_doc"
                              onClick={(event) => event.target.value}
                              id="contained-button-file"
                              type="file"
                              onChange={(value) => readFiles(value)}
                              hidden
                            />
                            <CloudUpload />
                          </IconButton>
                        </HtmlTooltip>
                      )}

                      <TextField
                        id="supplemental-doc-name"
                        variant="outlined"
                        size="small"
                        label="Document name"
                        fullWidth
                        disabled={!isSupplementalDoc}
                        value={isSupplementalDoc ? documentName : 'No document uploaded'}
                        onChange={handleDocumentNameChange}
                      />
                    </Box>
                    <Box display="flex" justifyContent="end">
                      {isSupplementalDoc ? (
                        <Typography variant="caption">
                          Uploaded {moment(supplementalDocument?.created_at, 'YYYY-MM-DD').format('MM/DD/YYYY')}
                        </Typography>
                      ) : (
                        <Typography variant="caption">File must be a PDF</Typography>
                      )}
                    </Box>
                  </>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                {isSupplementalDoc && isSupplementalDocChecked && (
                  <Box display="flex" flexDirection="column" gap="8px">
                    <BorderedSection title="Preview">
                      <FormControlLabel
                        control={<Checkbox disabled checked name="supplemental_doc_name_agreement" />}
                        label={
                          <Typography>
                            I have read, understood, and agree to{' '}
                            {supplementalDocument?.name?.length > 1 && (
                              <Link
                                target={isInPWAView ? '_self' : '_blank'}
                                to={generatePath(routes.supplementalDocument, {
                                  title: supplementalDocument?.name,
                                  src: supplementalDocument?.url,
                                })}
                              >
                                {supplementalDocument?.name}
                              </Link>
                            )}
                          </Typography>
                        }
                      />
                    </BorderedSection>
                    <Box>
                      <Alert severity="info">
                        The system will log signatures and the document name; make sure you include a version number in
                        the name of your document!
                      </Alert>
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </DashboardTableCard>
      )}
    </>
  );
};

export default SCDashboardSettings;
